<template>
  <div class="home-wrapper">

    <section class="section-spacing section-testimonials">
      <v-container>
        <div class="text-center mb-10">
          <h2 class="text-overline red--text font-weight-bold mb-2 tracking-widest">
            O QUE DIZEM SOBRE NÓS
          </h2>
          <h1 class="text-h5 text-md-h3 font-weight-black white--text text-uppercase">
            Fala Mergulhador!
          </h1>
          <div class="mx-auto gradient-line mt-4"></div>
        </div>
        
        <div v-if="loading" class="text-center py-6">
          <v-progress-circular indeterminate color="#D32F2F"></v-progress-circular>
        </div>

        <v-row justify="center" v-else-if="depoimentos.length">
          <v-col cols="12" md="10" lg="8">
            <v-carousel 
              v-model="depoimentoAtual" 
              hide-delimiters 
              cycle 
              :show-arrows="false"
              height="auto"
              class="py-2"
              interval="6000"
            >
              <v-carousel-item v-for="(depoimento, index) in depoimentos" :key="index">
                <div class="d-flex justify-center px-4">
                   <CardDepoimentos :depoimento="depoimento" class="transparent elevation-0"></CardDepoimentos>
                </div>
              </v-carousel-item>
            </v-carousel>
            
            <div class="d-flex justify-center mt-2">
              <v-btn 
                v-for="(n, i) in depoimentos.length" 
                :key="i"
                icon 
                x-small
                class="mx-1"
                :color="i === depoimentoAtual ? 'red' : 'grey darken-3'"
                @click="depoimentoAtual = i"
              >
                <v-icon>mdi-circle</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
        
        <p v-else class="text-center grey--text py-6">
          Ainda não temos depoimentos registrados.
        </p>
      </v-container>
    </section>

    <section class="section-spacing" style="background-color: #050505; border-top: 1px solid #222;">
      <v-container>
        <div class="text-center mb-6 mb-md-10">
          <h1 class="text-h5 text-md-h3 font-weight-black white--text text-uppercase">
            Veja em Vídeo
          </h1>
          <div class="mx-auto gradient-line mt-4"></div>
        </div>

        <v-slide-group
          class="pa-1 pa-md-4"
          :show-arrows="$vuetify.breakpoint.mdAndUp"
          dark
          center-active
        >
          <v-slide-item
            v-for="(video, index) in videosShorts"
            :key="index"
          >
            <v-card
              class="ma-2 ma-md-3 rounded-xl overflow-hidden elevation-10 video-card position-relative"
              :height="videoCardSize.height"
              :width="videoCardSize.width"
              color="#000"
            >
              <div v-if="!video.active" class="fill-height w-100 position-relative clickable-cover" @click="ativarVideo(index)">
                <v-img
                  :src="`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`"
                  height="100%"
                  width="100%"
                  cover
                  class="opacity-hover"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey darken-3"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
                
                <div class="play-icon-overlay d-flex justify-center align-center">
                   <v-btn fab large color="red" class="elevation-10">
                     <v-icon large color="white">mdi-play</v-icon>
                   </v-btn>
                </div>
              </div>

              <iframe
                v-else
                width="100%"
                height="100%"
                :src="`https://www.youtube.com/embed/${video.id}?rel=0&autoplay=1`"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                style="border-radius: 20px;"
              ></iframe>
            </v-card>
          </v-slide-item>
        </v-slide-group>
      </v-container>
    </section>

    <section class="section-spacing" style="background-color: #121212;">
      <v-container>
        <div class="text-center mb-10 mb-md-16">
          <h2 class="text-overline red--text text--lighten-1 font-weight-bold mb-2 tracking-widest">
            EVOLUA SUAS HABILIDADES
          </h2>
          <h1 class="text-h5 text-md-h2 font-weight-black white--text text-uppercase mb-4">
            Nossos Cursos
          </h1>
          <div class="mx-auto gradient-line"></div>
        </div>

        <v-row v-if="!loading" justify="center">
          <v-col 
            v-for="(categoria, index) in categorias" 
            :key="index" 
            cols="12" sm="6" md="6" lg="3"
            class="d-flex"
          >
            <v-hover v-slot="{ hover }">
              <v-card 
                class="course-card rounded-xl flex-grow-1 d-flex flex-column"
                :elevation="hover ? 24 : 6"
                color="#1a1a1a"
                :class="{ 'on-hover': hover }"
                @click="irParaCursos(categoria)"
              >
                <div class="image-wrapper rounded-t-xl">
                  <v-img 
                    :src="(categoria.volume && categoria.volume.url) ? categoria.volume.url : 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=2070&auto=format&fit=crop'" 
                    height="250px" 
                    cover
                    class="zoom-image"
                    :class="{ 'zoomed': hover }"
                  >
                    <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="red"></v-progress-circular>
                        </v-row>
                    </template>
                    <div class="image-gradient"></div>
                  </v-img>
                  <div class="hover-overlay" :style="{ opacity: hover ? 1 : 0 }"></div>
                </div>

                <div class="d-flex flex-column flex-grow-1 pa-6 relative z-index-2">
                  <h3 class="text-h5 font-weight-bold white--text mb-3 line-clamp-2">
                    {{ categoria.nome }}
                  </h3>
                  <p class="grey--text text--lighten-1 flex-grow-1 text-body-2 line-clamp-3">
                    {{ truncateText(categoria.descricao, 120) }}
                  </p>
                  <div class="mt-4">
                    <span class="red--text text-button font-weight-bold group-hover-underline">
                      {{ categoria.textoBotao ? categoria.textoBotao.toUpperCase() : 'SAIBA MAIS' }} 
                      <v-icon small color="red" class="ml-1">mdi-arrow-right</v-icon>
                    </span>
                  </div>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <PodcastSection />

    <section class="section-spacing position-relative" style="background-color: #050505;">
      <div class="bg-pattern"></div>
      <v-container class="position-relative z-index-2">
        <div class="text-center mb-10 mb-md-16">
          <v-icon size="40" color="#D32F2F" class="mb-4">mdi-camera-burst</v-icon>
          <h2 class="text-h5 text-md-h2 font-weight-black white--text text-uppercase">
            Lagão pelo mundo
          </h2>
          <p class="grey--text text--darken-1 mt-2 text-subtitle-1">
            Veja por onde já passamos e inspire-se para a próxima
          </p>
        </div>
        
        <div v-if="viagens.length" class="mx-auto">
          <v-slide-group
            class="pa-4"
            active-class="success"
            :show-arrows="$vuetify.breakpoint.mdAndUp"
            dark
            center-active
          >
            <v-slide-item
              v-for="(viagem, index) in viagens"
              :key="index"
            >
              <div class="ma-3" style="width: 350px; height: 500px">
                 <CardViagens 
                    :viagem="viagem" 
                    @abrir-detalhes="abrirDetalhesViagem"
                    class="elevation-10 rounded-xl"
                    style="height: 100%"
                 ></CardViagens>
              </div>
            </v-slide-item>
          </v-slide-group>
          
          <div class="text-center mt-8">
            <v-btn outlined color="white" large to="/viagens" class="px-8 hover-scale">
              Ver Portfólio Completo
            </v-btn>
          </div>
        </div>
        
        <div v-else class="text-center grey--text">
            Nenhuma viagem registrada recentemente.
        </div>
      </v-container>
    </section>

    <section class="section-spacing" style="background-color: #0F0F0F; border-top: 1px solid #222;">
      <v-container>
        <div class="text-center mb-12">
          <h2 class="text-overline red--text font-weight-bold mb-2 tracking-widest">
            AGENDA ABERTA
          </h2>
          <h1 class="text-h5 text-md-h2 font-weight-black white--text text-uppercase">
            Próximas Expedições
          </h1>
          <div class="mx-auto gradient-line mt-4"></div>
        </div>

        <v-row v-if="expedicoes.length" justify="center">
          <v-col 
            v-for="exp in expedicoes" :key="exp.id"
            cols="12" md="4"
            class="d-flex"
          >
            <v-hover v-slot="{ hover }">
              <v-card 
                class="course-card rounded-xl flex-grow-1 d-flex flex-column"
                :elevation="hover ? 24 : 6"
                color="#1a1a1a"
                :class="{ 'on-hover': hover }"
              >
                <div class="image-wrapper rounded-t-xl">
                  <v-img 
                    :src="exp.volume && exp.volume[0] ? exp.volume[0].url : 'https://via.placeholder.com/400'" 
                    height="250px" 
                    cover
                    class="zoom-image"
                    :class="{ 'zoomed': hover }"
                  >
                    <div class="d-flex justify-end pa-4">
                        <v-chip 
                          color="red darken-1" 
                          label 
                          class="font-weight-bold white--text elevation-4"
                        >
                          <v-icon left small>mdi-calendar</v-icon>
                          {{ formataData(exp.dataInicio) }}
                        </v-chip>
                    </div>
                    
                    <div class="image-gradient"></div>
                  </v-img>
                </div>

                <div class="d-flex flex-column flex-grow-1 pa-6 relative z-index-2">
                  <h3 class="text-h5 font-weight-bold white--text mb-2">
                    {{ exp.local }}
                  </h3>
                  
                  <p class="grey--text text--lighten-1 flex-grow-1 text-body-2 line-clamp-3">
                    {{ truncateText(exp.descricao, 120) }}
                  </p>

                  <v-divider class="grey darken-3 my-4"></v-divider>

                  <div class="d-flex align-center justify-end mt-auto">
                    <v-btn 
                      :color="hover ? 'red darken-1' : 'white'" 
                      :outlined="!hover"
                      :dark="hover"
                      rounded
                      class="px-8 font-weight-bold transition-swing"
                      @click.stop="abrirWhatsapp(exp)"
                    >
                      SAIBA MAIS
                      <v-icon right small>mdi-arrow-right</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
        
        <div v-else class="text-center grey--text">
            Em breve novas datas.
        </div>
      </v-container>
    </section>
    <template>
  <div>
    <PremiacoesSection />

    </div>
</template>

   <section class="section-spacing" style="background-color: #050505; border-top: 1px solid #222;">
      <v-container>
        <v-row justify="center" align="center">
          
          <v-col cols="10" md="5" class="d-none d-md-block">
            <v-img
              src="https://i.ibb.co/9Hb6QpnD/Whats-App-Image-2026-02-07-at-10-48-18.png"
              class="mx-auto"
              max-height="900"
              contain
              style="opacity: 1"
            ></v-img>
          </v-col>

          <v-col cols="12" md="6" lg="5">
            <div class="text-center mb-10">
              <h2 class="text-overline red--text font-weight-bold mb-2 tracking-widest">
                FALE CONOSCO
              </h2>
              <h1 class="text-h5 text-md-h3 font-weight-black white--text text-uppercase">
                Entre em contato
              </h1>
              <div class="mx-auto gradient-line mt-4"></div>
              <p class="grey--text mt-4">
                Preencha o formulário abaixo e entraremos em contato.
              </p>
            </div>

            <v-form ref="form" v-model="formValido" lazy-validation>
              <v-text-field
                v-model="form.nome"
                :rules="[v => !!v || 'Nome é obrigatório']"
                label="Seu Nome"
                outlined
                dark
                color="red"
                background-color="#121212"
              ></v-text-field>

              <v-text-field
                v-model="form.email"
                :rules="[
                  v => !!v || 'E-mail é obrigatório',
                  v => /.+@.+\..+/.test(v) || 'E-mail inválido'
                ]"
                label="Seu E-mail"
                outlined
                dark
                color="red"
                background-color="#121212"
              ></v-text-field>

              <v-text-field
                v-model="form.telefone"
                label="Seu WhatsApp / Telefone (Opcional)"
                outlined
                dark
                color="red"
                background-color="#121212"
                type="tel"
              ></v-text-field>

              <v-textarea
                v-model="form.mensagem"
                :rules="[v => !!v || 'Mensagem é obrigatória']"
                label="Sua Mensagem"
                outlined
                dark
                color="red"
                background-color="#121212"
                rows="4"
              ></v-textarea>

              <div class="text-center mt-4">
                <v-btn
                  :loading="enviando"
                  :disabled="!formValido"
                  color="red darken-1"
                  x-large
                  block
                  class="white--text font-weight-bold"
                  @click="enviarContato"
                >
                  ENVIAR MENSAGEM
                  <v-icon right>mdi-send</v-icon>
                </v-btn>
              </div>
            </v-form>
          </v-col>
        </v-row>
      </v-container>
      
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top timeout="4000">
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Fechar</v-btn>
        </template>
      </v-snackbar>
    </section>

    <v-dialog v-model="dialogDetalhes" max-width="900px" scrollable>
        <v-card v-if="viagemSelecionada" color="#1E1E1E" class="rounded-xl overflow-hidden">
          
          <v-toolbar color="transparent" flat class="pt-2 px-2">
            <v-spacer></v-spacer>
            <v-btn icon dark @click="dialogDetalhes = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="pa-0">
            <v-container>
              <v-row>
                <v-col cols="12" class="text-center mb-6">
                  <div class="d-flex justify-center align-center mb-2">
                    <v-icon color="#D32F2F" class="mr-2">mdi-map-marker</v-icon>
                    <span class="text-overline grey--text text--lighten-1 tracking-widest">
                      {{ formataData(viagemSelecionada.inicioViagem) }}
                    </span>
                  </div>
                  <h2 class="text-h3 font-weight-black white--text text-uppercase mb-4">
                    {{ viagemSelecionada.destino }}
                  </h2>
                  <div class="mx-auto gradient-line mb-6"></div>
                  <p class="grey--text text--lighten-1 text-body-1" style="max-width: 800px; margin: 0 auto; line-height: 1.8;">
                    {{ viagemSelecionada.descricao }}
                  </p>
                </v-col>

                <v-col cols="12">
                  <h3 class="text-h5 white--text font-weight-bold mb-4 ml-2 border-left-red pl-4">
                    Galeria de Fotos
                  </h3>
                  
                  <div v-if="!temFotos(viagemSelecionada)" class="text-center py-10 grey--text darken-2">
                    <v-icon size="40" color="grey darken-3">mdi-camera-off</v-icon>
                    <p class="mt-2">Nenhuma foto disponível.</p>
                  </div>

                  <v-row v-else>
                     <v-col 
                        v-for="(foto, i) in viagemSelecionada.volume" 
                        :key="i"
                        cols="12" sm="6" md="4"
                     >
                        <v-card 
                          class="rounded-lg overflow-hidden photo-card" 
                          hover
                          @click="abrirFotoGrande(i)"
                        >
                          <v-img 
                            :src="foto.url" 
                            aspect-ratio="1.5" 
                            cover
                            class="zoom-hover"
                          >
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="#D32F2F"></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                        </v-card>
                     </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>

          <v-card-actions class="justify-center pb-6">
            <v-btn rounded large color="#D32F2F" dark class="px-8 font-weight-bold" @click="dialogDetalhes = false">
              Fechar Galeria
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogFoto" fullscreen hide-overlay transition="fade-transition">
        <v-card color="black" dark class="d-flex flex-column justify-center align-center">
          <v-btn icon absolute top right large @click="dialogFoto = false" class="mt-4 mr-4 z-index-10">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          
          <v-carousel 
            v-if="viagemSelecionada && temFotos(viagemSelecionada)"
            v-model="fotoIndex"
            hide-delimiters
            height="100%"
            style="width: 100%;"
          >
            <v-carousel-item
              v-for="(foto, i) in viagemSelecionada.volume"
              :key="i"
              :src="foto.url"
              contain
            ></v-carousel-item>
          </v-carousel>
        </v-card>
      </v-dialog>

  </div>
</template>

<script>
import CardViagens from '@/components/viagens/CardViagens.vue';
import CardDepoimentos from "@/components/depoimentos/CardDepoimentos.vue";
import PodcastSection from '@/components/home/PodcastSection.vue';

import viagensService from '@/services/viagens.service';
import depoimentosService from '@/services/depoimentos.service';
import expedicoesService from '@/services/expedicoes.service';
import PremiacoesSection from '@/components/PremiacoesSection.vue';
import categoriaCursosService from '@/services/categoriacurso.service';
import { mutations } from "@/store";

import axios from 'axios';

export default {
  name: "IndexHome",
  components: {
    CardViagens,
    PodcastSection,
    CardDepoimentos,
    PremiacoesSection
  },
  data() {
    return {
      loading: true,
      viagens: [],
      expedicoes: [],
      categorias: [],
      depoimentos: [],
      depoimentoAtual: 0,
      
      // Controle do Modal
      dialogDetalhes: false,
      viagemSelecionada: null,
      dialogFoto: false,
      fotoIndex: 0,
      
      videosShorts: [
        { id: '6Ig-3W5aSZM', active: false },
        { id: 'IX7AxK0lABg', active: false },
        { id: 'U5eXkAJz9cE', active: false },
        { id: 'BALPtn3eZdE', active: false },
        { id: '8tBpNbmTWxM', active: false },
        { id: 'PuWkzzwUgiM', active: false },
        { id: 'qxRv4mQOPFk', active: false },
        { id: 'NA9N7IZFFbQ', active: false },
        { id: 'Z6AUABgxrtY', active: false },
        { id: '3UDcgo1cqaI', active: false },
        { id: 'KDC0o009Vi8', active: false },
        { id: '3X2d1amX3zg', active: false }
      ],
      
      enviando: false,
      formValido: true,
      form: {
        nome: '',
        email: '',
        telefone: '',
        mensagem: ''
      },
      snackbar: {
        show: false,
        text: '',
        color: ''
      }
    };
  },
  computed: {
    videoCardSize() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs': return { width: 220, height: 390 };
        case 'sm': return { width: 240, height: 426 };
        default:   return { width: 280, height: 500 };
      }
    }
  },
  methods: {
    ativarVideo(index) {
        this.videosShorts[index].active = true;
    },
    
    // --- MÉTODOS DO MODAL ---
    abrirDetalhesViagem(viagem) {
      this.viagemSelecionada = viagem;
      this.dialogDetalhes = true;
    },
    temFotos(viagem) {
      return viagem && viagem.volume && Array.isArray(viagem.volume) && viagem.volume.length > 0;
    },
    abrirFotoGrande(index) {
      this.fotoIndex = index;
      this.dialogFoto = true;
    },

    async enviarContato() {
      if (!this.$refs.form.validate()) return;
      this.enviando = true;

      try {
        const urlFormspree = 'https://formspree.io/f/xvzboqpv'; 
        
        await axios.post(urlFormspree, {
          email: this.form.email,
          message: `
            Nome: ${this.form.nome}
            Email: ${this.form.email}
            Telefone: ${this.form.telefone || 'Não informado'}
            
            Mensagem:
            ${this.form.mensagem}
          `
        });

        this.snackbar = {
          show: true,
          text: 'Mensagem enviada! Em breve entraremos em contato.',
          color: 'success'
        };
        this.$refs.form.reset();
      } catch (error) {
        console.error(error);
        this.snackbar = {
          show: true,
          text: 'Ocorreu um erro ao enviar. Tente novamente.',
          color: 'error'
        };
      } finally {
        this.enviando = false;
      }
    },
    abrirWhatsapp(expedicao) {
      const numero = "61998385830";
      const mensagem = `Olá, desejo mais informações a respeito da expedição *${expedicao.local}*`;
      const url = `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(mensagem)}`;
      window.open(url, '_blank');
    },
    formataData(data) {
        if(!data) return '';
        const date = new Date(data);
        return date.toLocaleDateString('pt-BR');
    },
    irParaCursos(categoria) {
      mutations.setCategoria(categoria);
      this.$router.push({ name: 'Cursos' });
    },
    truncateText(text, length) {
      if (!text || typeof text !== 'string') return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    async pegarDados() {
      try {
        this.loading = true;
        const [viagensRes, categoriasData, expedicoesRes, depoimentosData] = await Promise.all([
          viagensService.getAll().catch(() => ({ data: [] })), 
          categoriaCursosService.getCategoriaCursos().catch(() => []),
          expedicoesService.getAll().catch(() => ({ data: [] })), 
          depoimentosService.getDepoimentos().catch(() => [])
        ]);

        const todasViagens = viagensRes.data || [];
        // Pega as 5 mais recentes
        this.viagens = todasViagens
            .sort((a, b) => new Date(b.inicioViagem) - new Date(a.inicioViagem))
            .slice(0, 5);

        this.categorias = categoriasData;
        this.depoimentos = depoimentosData;
        this.expedicoes = expedicoesRes.data || [];
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.pegarDados();
  }
};
</script>

<style scoped>
/* ==============================
   BASE & LAYOUT
   ============================== */
.home-wrapper {
  background-color: #121212;
  font-family: 'Roboto', sans-serif;
  min-height: 100vh;
  width: 100%; 
  overflow-x: hidden;
  position: relative;  
}

.section-spacing {
  padding-top: 60px;
  padding-bottom: 60px;
}
@media (min-width: 960px) {
  .section-spacing {
    padding-top: 100px;
    padding-bottom: 100px;
  }
}

.tracking-widest { letter-spacing: 3px !important; }
.z-index-2 { z-index: 2; position: relative; }

/* Linha Gradiente */
.gradient-line {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #D32F2F 0%, #ff7961 100%);
  border-radius: 4px;
}

/* ==============================
   SEÇÃO DEPOIMENTOS
   ============================== */
.section-testimonials {
  background: linear-gradient(to bottom, #000000 0%, #121212 100%);
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

/* ==============================
   ESTILOS DOS VÍDEOS
   ============================== */
.video-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.3s ease, border-color 0.3s ease;
}
.video-card:hover {
  border-color: #D32F2F;
}

/* Capa Clicável */
.clickable-cover {
  cursor: pointer;
  transition: opacity 0.3s;
}
.clickable-cover:hover .opacity-hover {
  opacity: 0.8;
}

/* Ícone de Play centralizado */
.play-icon-overlay {
  position: absolute;
  top: 0; left: 0; 
  width: 100%; height: 100%;
  pointer-events: none;
  z-index: 2;
}

/* ==============================
   CARD DE CURSOS
   ============================== */
.course-card {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  background: #1a1a1a;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  overflow: hidden;
}

.course-card.on-hover {
  transform: translateY(-12px);
  border-color: #D32F2F !important;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.5) !important;
}

.course-card.on-hover .group-hover-underline {
  text-decoration: underline;
}

.image-wrapper {
  position: relative;
  overflow: hidden;
  height: 250px;
}

.zoom-image { transition: transform 0.6s ease; }
.zoom-image.zoomed { transform: scale(1.1); }

.image-gradient {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background: linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.9) 100%);
  pointer-events: none;
}

.hover-overlay {
  position: absolute; 
  top: 0; left: 0; width: 100%; height: 100%; 
  background: rgba(211, 47, 47, 0.2); 
  pointer-events: none; 
  transition: opacity 0.3s;
}

/* Texto Limitado */
.line-clamp-2 {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.line-clamp-3 {
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
}

/* ==============================
   SEÇÃO VIAGENS
   ============================== */
.bg-pattern {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  opacity: 0.03;
  background-image: radial-gradient(#ffffff 1px, transparent 1px);
  background-size: 30px 30px;
  pointer-events: none;
}

.hover-scale {
    transition: transform 0.3s ease;
}
.hover-scale:hover {
    transform: scale(1.05);
}

/* ESTILOS EXTRAS DO MODAL */
.border-left-red {
  border-left: 4px solid #D32F2F;
}
.photo-card {
  border: 1px solid rgba(255,255,255,0.1);
  cursor: pointer;
}
.zoom-hover {
  transition: transform 0.4s ease;
}
.photo-card:hover .zoom-hover {
  transform: scale(1.05);
}
.z-index-10 {
  z-index: 10;
}
</style>